import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Typography,
    Grid,
    IconButton,
    Card,
    CardContent,
    Chip,
    Box,
    InputAdornment,
    CircularProgress,
} from '@mui/material';
import {
    ArrowBack,
    LocationOn,
    CreditCard,
    Person,
    Business,
    Smartphone,
    AccountBalance,
    Language,
    Search,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CloseIcon from '../CustomSVGIcons/CloseIcon';
import { getExternalAccountInfo, createExternalAccount } from '../../redux/account/actions';
import { RootState } from '../../redux/root-reducer';
import { IBankInfo } from '../../redux/account/types';
import { showToastMessage } from '../../utils/AppUtils';

// Sample data for countries
const countries = [
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
    { code: 'GH', name: 'Ghana', flag: '🇬🇭' },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
    // Europe
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
    { code: 'NO', name: 'Norway', flag: '🇳🇴' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
    // Asia
    { code: 'JP', name: 'Japan', flag: '🇯🇵' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
    { code: 'CN', name: 'China', flag: '🇨🇳' },
    { code: 'IN', name: 'India', flag: '🇮🇳' },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
    { code: 'HK', name: 'Hong Kong', flag: '🇭🇰' },
    { code: 'TH', name: 'Thailand', flag: '🇹🇭' },
    { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
    { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
    { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
    { code: 'VN', name: 'Vietnam', flag: '🇻🇳' },
    // Americas
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
    { code: 'CL', name: 'Chile', flag: '🇨🇱' },
    { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
    { code: 'PE', name: 'Peru', flag: '🇵🇪' },
    // Middle East & Africa
    { code: 'AE', name: 'UAE', flag: '🇦🇪' },
    { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: 'IL', name: 'Israel', flag: '🇮🇱' },
    { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
    { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
    { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
    { code: 'MA', name: 'Morocco', flag: '🇲🇦' },
    // Oceania
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
];

// Sample data for South African banks with codes
const southAfricanBanks = [
    { name: 'Standard Bank', code: '051001' },
    { name: 'FNB', code: '250655' },
    { name: 'ABSA', code: '632005' },
    { name: 'Nedbank', code: '198765' },
    { name: 'Capitec Bank', code: '470010' },
    { name: 'African Bank', code: '430000' },
    { name: 'Investec', code: '580105' },
    { name: 'Discovery Bank', code: '679000' },
    { name: 'TymeBank', code: '678910' },
    { name: 'Bidvest Bank', code: '462005' },
];

interface PaymentMethod {
    id: string;
    name: string;
    description: string;
    icon: React.ReactNode;
}

const getPaymentMethods = (countryCode: string): PaymentMethod[] => {
    const wireTransfer = {
        id: 'wire',
        name: 'Wire Transfer',
        description: 'International bank transfer',
        icon: <Language />,
    };

    switch (countryCode) {
        case 'US':
            return [
                {
                    id: 'ach',
                    name: 'ACH Transfer',
                    description: 'Direct bank transfer within US',
                    icon: <Business />,
                },
                wireTransfer,
            ];
        case 'NG':
            return [
                {
                    id: 'nuban',
                    name: 'NUBAN Transfer',
                    description: 'Nigerian bank account transfer',
                    icon: <CreditCard />,
                },
                wireTransfer,
            ];
        case 'GH':
            return [
                {
                    id: 'mobile_money',
                    name: 'Mobile Money',
                    description: 'Mobile wallet transfer',
                    icon: <Smartphone />,
                },
                wireTransfer,
            ];
        case 'ZA':
            return [
                {
                    id: 'eft',
                    name: 'EFT Transfer',
                    description: 'Electronic Funds Transfer',
                    icon: <AccountBalance />,
                },
                wireTransfer,
            ];
        default:
            return [wireTransfer];
    }
};

interface ExternalAccountModalProps {
    open: boolean;
    onClose: () => void;
}

const ExternalAccountModal: React.FC<ExternalAccountModalProps> = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const { themeStyle, device } = useThemeContext();
    const [step, setStep] = useState<'country' | 'payment' | 'details'>('country');
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
    const [isAnimating, setIsAnimating] = useState(false);
    const [formData, setFormData] = useState<any>({});
    const [countrySearch, setCountrySearch] = useState<string>('');

    // Redux state
    const { externalAccountInfo, gettingExternalAccountInfo, creatingExternalAccount, userExternalAccounts } =
        useSelector((state: RootState) => state.account);

    // Fetch external account info when modal opens
    useEffect(() => {
        if (open && !externalAccountInfo) {
            dispatch(getExternalAccountInfo());
        }
    }, [open, dispatch, externalAccountInfo]);

    // Track if we just successfully created an account
    const [accountCreated, setAccountCreated] = useState(false);

    // Close modal when account is successfully created
    useEffect(() => {
        if (!creatingExternalAccount && accountCreated) {
            const timer = setTimeout(() => {
                handleClose();
                setAccountCreated(false);
            }, 1500);

            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creatingExternalAccount, accountCreated]);

    // Get banks for selected country
    const getBanksForCountry = (countryCode: string): IBankInfo[] => {
        if (!externalAccountInfo) return [];

        switch (countryCode) {
            case 'NG':
                return externalAccountInfo.NG || [];
            case 'GH':
                return externalAccountInfo.GH || [];
            case 'KY':
                return externalAccountInfo.KY || [];
            default:
                return [];
        }
    };

    // Filter countries based on search
    const filteredCountries = countries.filter(
        country =>
            country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
            country.code.toLowerCase().includes(countrySearch.toLowerCase()),
    );

    const handleCountrySearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCountrySearch(event.target.value);
    };

    const handleCountrySelect = (countryCode: string) => {
        setSelectedCountry(countryCode);
        setIsAnimating(true);
        setTimeout(() => {
            setStep('payment');
            setIsAnimating(false);
        }, 300);
    };

    const handlePaymentMethodSelect = (methodId: string) => {
        setSelectedPaymentMethod(methodId);
        setIsAnimating(true);
        setTimeout(() => {
            setStep('details');
            setIsAnimating(false);
        }, 300);
    };

    const handleBack = () => {
        setIsAnimating(true);
        setTimeout(() => {
            if (step === 'details') {
                setStep('payment');
                setSelectedPaymentMethod('');
                setAccountCreated(false);
            } else if (step === 'payment') {
                setStep('country');
                setSelectedCountry('');
            }
            setIsAnimating(false);
        }, 300);
    };

    const handleClose = () => {
        setStep('country');
        setSelectedCountry('');
        setSelectedPaymentMethod('');
        setIsAnimating(false);
        setFormData({});
        setAccountCreated(false);
        onClose();
    };

    const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [field]: event.target.value });
    };

    const handleSelectChange = (field: string) => (event: any) => {
        setFormData({ ...formData, [field]: event.target.value });
    };

    const handleSubmit = () => {
        // Check if user already has 3 accounts
        if (userExternalAccounts && userExternalAccounts.length >= 3) {
            showToastMessage('Maximum 3 accounts allowed. Please delete an existing account first.', 'error');
            return;
        }

        // Prepare the request payload based on the selected payment method
        const payload: any = {
            name: formData.accountHolderName || 'External Account',
            country: selectedCountry,
            account_currency: 'USD', // Default to USD as per requirements
        };

        switch (selectedPaymentMethod) {
            case 'nuban':
                payload.external_account_type = 'nuban';
                const selectedBank = getBanksForCountry(selectedCountry).find(bank => bank.name === formData.bankName);
                payload.bank_details = {
                    account_number: parseInt(formData.accountNumber),
                    bank_name: formData.bankName,
                    bank_code: selectedBank?.code || '',
                };
                break;
            case 'mobile_money':
                payload.external_account_type = 'mobile_money';
                const selectedProvider = getBanksForCountry(selectedCountry).find(
                    provider => provider.name === formData.mobileProvider,
                );
                payload.mobile_money_details = {
                    phone_number: formData.mobileNumber,
                    name: formData.accountHolderName,
                    address: formData.bankAddress || '',
                    mobile_money_code: selectedProvider?.code || '',
                    mobile_money_name: formData.mobileProvider,
                };
                break;
            case 'wire':
                payload.external_account_type = 'bank_account';
                payload.bank_details = {
                    routing_number: parseInt(formData.routingNumber),
                    account_number: parseInt(formData.accountNumber),
                    address: formData.bankAddress || '',
                };
                break;
            default:
                console.error('Unknown payment method:', selectedPaymentMethod);
                return;
        }

        setAccountCreated(true);
        dispatch(createExternalAccount(payload));
    };

    const renderCountrySelection = () => (
        <Box
            sx={{
                opacity: isAnimating ? 0 : 1,
                transform: isAnimating ? 'translateX(20px)' : 'translateX(0)',
                transition: 'all 0.3s ease',
                spaceY: 3,
            }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                    sx={{
                        mx: 'auto',
                        width: 48,
                        height: 48,
                        bgcolor: theme[themeStyle].darkGray60,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                    }}>
                    <LocationOn sx={{ color: theme[themeStyle].tag_white, fontSize: 24 }} />
                </Box>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: theme[themeStyle].tag_white,
                    }}>
                    Select Your Country
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: theme[themeStyle].light_gray_text,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                    }}>
                    Choose your country to see available payment options
                </Typography>
            </Box>

            {/* Search Input */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search countries..."
                    value={countrySearch}
                    onChange={handleCountrySearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: theme[themeStyle].placeholderGray }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: theme[themeStyle].form_divider,
                            },
                            '&:hover fieldset': {
                                borderColor: theme[themeStyle].highlight,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: theme[themeStyle].highlight,
                            },
                            backgroundColor: theme[themeStyle].form_fields,
                            color: theme[themeStyle].tag_white,
                        },
                        '& .MuiInputBase-input': {
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            color: theme[themeStyle].tag_white,
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: theme[themeStyle].placeholderGray,
                            opacity: 1,
                        },
                    }}
                />
            </Box>

            <Box
                sx={{
                    height: 320,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                {filteredCountries.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 4,
                            color: theme[themeStyle].light_gray_text,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <Typography variant="body2">No countries found matching "{countrySearch}"</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={1} sx={{ flex: 1 }}>
                        {filteredCountries.map(country => (
                            <Grid item xs={6} sm={4} key={country.code}>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        backgroundColor: theme[themeStyle].secondaryBg,
                                        border: `1px solid ${theme[themeStyle].selectableBorder}`,
                                        '&:hover': {
                                            boxShadow: `0px 4px 8px ${theme[themeStyle].shadowColor}`,
                                            transform: 'scale(1.05)',
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    }}
                                    onClick={() => handleCountrySelect(country.code)}>
                                    <CardContent sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="h4" sx={{ mb: 1 }}>
                                            {country.flag}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                fontSize: '0.75rem',
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: theme[themeStyle].tag_white,
                                            }}>
                                            {country.name}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Box>
    );

    const renderPaymentMethodSelection = () => {
        const paymentMethods = getPaymentMethods(selectedCountry);
        const selectedCountryData = countries.find(c => c.code === selectedCountry);

        return (
            <Box
                sx={{
                    opacity: isAnimating ? 0 : 1,
                    transform: isAnimating ? 'translateX(20px)' : 'translateX(0)',
                    transition: 'all 0.3s ease',
                    spaceY: 3,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <IconButton
                        onClick={handleBack}
                        size="small"
                        sx={{
                            color: theme[themeStyle].tag_white,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].button_background,
                            },
                        }}>
                        <ArrowBack />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h5">{selectedCountryData?.flag}</Typography>
                        <Chip
                            label={selectedCountryData?.name}
                            variant="outlined"
                            size="small"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                borderColor: theme[themeStyle].highlight,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}
                        />
                    </Box>
                </Box>

                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                        sx={{
                            mx: 'auto',
                            width: 48,
                            height: 48,
                            bgcolor: theme[themeStyle].secondaryBg,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                        }}>
                        <AccountBalance sx={{ color: theme[themeStyle].tag_white, fontSize: 24 }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            mb: 1,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: theme[themeStyle].tag_white,
                        }}>
                        Choose Payment Method
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme[themeStyle].light_gray_text,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        Select how you'd like to receive payments
                    </Typography>
                </Box>

                <Box sx={{ spaceY: 2 }}>
                    {paymentMethods.map(method => (
                        <Card
                            key={method.id}
                            sx={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                backgroundColor: theme[themeStyle].secondaryBg,
                                border: `1px solid ${theme[themeStyle].selectableBorder}`,
                                '&:hover': {
                                    boxShadow: `0px 4px 8px ${theme[themeStyle].shadowColor}`,
                                    transform: 'scale(1.02)',
                                    borderColor: theme[themeStyle].highlight,
                                },
                                mb: 1,
                            }}
                            onClick={() => handlePaymentMethodSelect(method.id)}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ color: theme[themeStyle].highlight }}>{method.icon}</Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 500,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: theme[themeStyle].tag_white,
                                            }}>
                                            {method.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme[themeStyle].light_gray_text,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            }}>
                                            {method.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Box>
        );
    };

    const renderDetailsForm = () => {
        const selectedCountryData = countries.find(c => c.code === selectedCountry);
        const paymentMethods = getPaymentMethods(selectedCountry);
        const selectedMethod = paymentMethods.find(m => m.id === selectedPaymentMethod);

        return (
            <Box
                sx={{
                    opacity: isAnimating ? 0 : 1,
                    transform: isAnimating ? 'translateX(20px)' : 'translateX(0)',
                    transition: 'all 0.3s ease',
                    spaceY: 3,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <IconButton
                        onClick={handleBack}
                        size="small"
                        sx={{
                            color: theme[themeStyle].tag_white,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].button_background,
                            },
                        }}>
                        <ArrowBack />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h5">{selectedCountryData?.flag}</Typography>
                        <Chip
                            label={selectedMethod?.name}
                            variant="outlined"
                            size="small"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                borderColor: theme[themeStyle].highlight,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}
                        />
                    </Box>
                </Box>

                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                        sx={{
                            mx: 'auto',
                            width: 48,
                            height: 48,
                            bgcolor: theme[themeStyle].secondaryBg,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                        }}>
                        <Person sx={{ color: theme[themeStyle].tag_white, fontSize: 24 }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            mb: 1,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].tag_white,
                        }}>
                        Account Details
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme[themeStyle].light_gray_text,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        Enter your account information
                    </Typography>
                </Box>

                <Box component="form" sx={{ spaceY: 3, flexDirection: 'column', gap: 2 }}>
                    {/* Common fields */}
                    <TextField
                        label="Account Holder Name"
                        fullWidth
                        placeholder="Enter full name as on account"
                        value={formData.accountHolderName || ''}
                        onChange={handleInputChange('accountHolderName')}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: theme[themeStyle].form_divider,
                                },
                                '&:hover fieldset': {
                                    borderColor: theme[themeStyle].highlight,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: theme[themeStyle].highlight,
                                },
                                backgroundColor: theme[themeStyle].form_fields,
                                color: theme[themeStyle].tag_white,
                            },
                            '& .MuiInputLabel-root': {
                                color: theme[themeStyle].form_label,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                            '& .MuiInputBase-input': {
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                color: theme[themeStyle].tag_white,
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: theme[themeStyle].placeholderGray,
                                opacity: 1,
                            },
                            mb: 1,
                        }}
                    />

                    {/* US ACH Transfer */}
                    {selectedPaymentMethod === 'ach' && (
                        <>
                            <TextField
                                label="Routing Number"
                                fullWidth
                                placeholder="9-digit routing number"
                                value={formData.routingNumber || ''}
                                onChange={handleInputChange('routingNumber')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                    mb: 1,
                                }}
                            />
                            <TextField
                                label="Account Number"
                                fullWidth
                                placeholder="Account number"
                                value={formData.accountNumber || ''}
                                onChange={handleInputChange('accountNumber')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                    mb: 1,
                                }}
                            />
                            <FormControl fullWidth>
                                <InputLabel
                                    sx={{
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Account Type
                                </InputLabel>
                                <Select
                                    value={formData.accountType || ''}
                                    onChange={handleSelectChange('accountType')}
                                    label="Account Type"
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].button_background,
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                    }}>
                                    <MenuItem value="checking">Checking</MenuItem>
                                    <MenuItem value="savings">Savings</MenuItem>
                                </Select>
                            </FormControl>
                        </>
                    )}

                    {/* Nigerian NUBAN Transfer */}
                    {selectedPaymentMethod === 'nuban' && (
                        <>
                            <FormControl fullWidth>
                                <InputLabel
                                    sx={{
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Bank Name
                                </InputLabel>
                                <Select
                                    value={formData.bankName || ''}
                                    onChange={handleSelectChange('bankName')}
                                    label="Bank Name"
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].button_background,
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                        mb: 1,
                                    }}>
                                    {gettingExternalAccountInfo ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Loading banks...
                                        </MenuItem>
                                    ) : (
                                        getBanksForCountry(selectedCountry).map(bank => (
                                            <MenuItem key={bank.id} value={bank.name}>
                                                {bank.name}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Account Number"
                                fullWidth
                                placeholder="10-digit NUBAN account number"
                                inputProps={{ maxLength: 10 }}
                                value={formData.accountNumber || ''}
                                onChange={handleInputChange('accountNumber')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                }}
                            />
                        </>
                    )}

                    {/* Ghanaian Mobile Money */}
                    {selectedPaymentMethod === 'mobile_money' && (
                        <>
                            <FormControl fullWidth>
                                <InputLabel
                                    sx={{
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Mobile Money Provider
                                </InputLabel>
                                <Select
                                    value={formData.mobileProvider || ''}
                                    onChange={handleSelectChange('mobileProvider')}
                                    label="Mobile Money Provider"
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].button_background,
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                        mb: 1,
                                    }}>
                                    {gettingExternalAccountInfo ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Loading providers...
                                        </MenuItem>
                                    ) : (
                                        getBanksForCountry(selectedCountry).map(provider => (
                                            <MenuItem key={provider.id} value={provider.name}>
                                                {provider.name}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Mobile Number"
                                fullWidth
                                placeholder="Mobile money number"
                                value={formData.mobileNumber || ''}
                                onChange={handleInputChange('mobileNumber')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                    mb: 1,
                                }}
                            />
                            <TextField
                                label="Account Name"
                                fullWidth
                                placeholder="Name registered with mobile money"
                                value={formData.accountName || ''}
                                onChange={handleInputChange('accountName')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                }}
                            />
                        </>
                    )}

                    {/* South African EFT */}
                    {selectedPaymentMethod === 'eft' && (
                        <>
                            <FormControl fullWidth>
                                <InputLabel
                                    sx={{
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Bank Name
                                </InputLabel>
                                <Select
                                    value={formData.bankName || ''}
                                    onChange={handleSelectChange('bankName')}
                                    label="Bank Name"
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].button_background,
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                    }}>
                                    {southAfricanBanks.map(bank => (
                                        <MenuItem key={bank.code} value={bank.name}>
                                            {bank.name} ({bank.code})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Account Number"
                                fullWidth
                                placeholder="Account number"
                                value={formData.accountNumber || ''}
                                onChange={handleInputChange('accountNumber')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                }}
                            />
                            <TextField
                                label="Branch Code"
                                fullWidth
                                placeholder="6-digit branch code"
                                value={formData.branchCode || ''}
                                onChange={handleInputChange('branchCode')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                }}
                            />
                        </>
                    )}

                    {/* Wire Transfer (common for all) */}
                    {selectedPaymentMethod === 'wire' && (
                        <>
                            <TextField
                                label="Bank Name"
                                fullWidth
                                placeholder="Full bank name"
                                value={formData.bankName || ''}
                                onChange={handleInputChange('bankName')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                    mb: 1,
                                }}
                            />
                            <TextField
                                label="SWIFT/BIC Code"
                                fullWidth
                                placeholder="Bank SWIFT code"
                                value={formData.swiftCode || ''}
                                onChange={handleInputChange('swiftCode')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                    mb: 1,
                                }}
                            />
                            <TextField
                                label="Account Number"
                                fullWidth
                                placeholder="International account number"
                                value={formData.accountNumber || ''}
                                onChange={handleInputChange('accountNumber')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                    mb: 1,
                                }}
                            />
                            <TextField
                                label="Bank Address"
                                fullWidth
                                placeholder="Bank's full address"
                                value={formData.bankAddress || ''}
                                onChange={handleInputChange('bankAddress')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                    mb: 1,
                                }}
                            />
                        </>
                    )}

                    <Box sx={{ pt: 2, spaceY: 2 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={handleSubmit}
                            disabled={creatingExternalAccount}
                            sx={{
                                backgroundColor: theme[themeStyle].primary_orange,
                                color: theme[themeStyle].black,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].highlight,
                                },
                                '&:disabled': {
                                    backgroundColor: theme[themeStyle].form_divider,
                                    color: theme[themeStyle].placeholderGray,
                                },
                                mb: 1,
                            }}>
                            {creatingExternalAccount ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: theme[themeStyle].black }} />
                                    Creating Account...
                                </>
                            ) : (
                                'Save Account Details'
                            )}
                        </Button>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleClose}
                            sx={{
                                borderColor: theme[themeStyle].highlight,
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '&:hover': {
                                    borderColor: theme[themeStyle].highlight,
                                    backgroundColor: theme[themeStyle].button_background,
                                },
                            }}>
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={device.isMobile}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: theme[themeStyle].deep_grey_lighter,
                    backgroundImage: 'none',
                    minHeight: device.isMobile ? '100vh' : 600,
                    maxHeight: device.isMobile ? '100vh' : '90vh',
                },
            }}>
            <DialogTitle
                sx={{
                    textAlign: 'center',
                    color: theme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    borderBottom: `1px solid ${theme[themeStyle].form_divider}`,
                    position: 'relative',
                    pr: 6, // Add padding for close button
                }}>
                Account Setup
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: theme[themeStyle].tag_white,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].button_background,
                        },
                    }}>
                    <CloseIcon
                        fill={theme[themeStyle].tag_white}
                        stroke={theme[themeStyle].tag_white}
                        width="20px"
                        height="20px"
                    />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    pt: 3,
                    backgroundColor: theme[themeStyle].form_background,
                }}>
                {step === 'country' && renderCountrySelection()}
                {step === 'payment' && renderPaymentMethodSelection()}
                {step === 'details' && renderDetailsForm()}
            </DialogContent>
        </Dialog>
    );
};

export default ExternalAccountModal;
